
import { SupabaseClient } from "@supabase/supabase-js";

interface CertificateData {
  recipientName: string;
  courseTitle: string;
  completionDate: string;
  hours: number;
  presenter: string;
  location: string;
  time: string;
  stateRequirements: any;
}

export const generateCertificateHtml = (certificateData: CertificateData) => `
  <!DOCTYPE html>
  <html>
    <head>
      <title>Certificate - ${certificateData.courseTitle}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@400;600&display=swap" rel="stylesheet">
      <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap" rel="stylesheet">
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap" rel="stylesheet">
      <link href="https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500;1,600;1,700;1,800&display=swap" rel="stylesheet">
      <link href="https://use.typekit.net/ijf8vgk.css" rel="stylesheet">
      <script src="https://cdn.tailwindcss.com"></script>
      <style>
        body {
          margin: 0;
          padding: 0;
          font-family: 'Garamond', 'Candara', serif;
          background: white;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        h1, h2, h3, h4 {
          font-family: 'Garamond', 'Candara', serif;
          font-weight: bold;
        }
        
        .font-title {
          font-family: 'Proxima Nova', 'proxima-nova', 'Inter', sans-serif;
          font-weight: bold;
        }
        
        .font-subtitle {
          font-family: 'Proxima Nova', 'proxima-nova', 'Inter', sans-serif;
        }
        
        .font-heading {
          font-family: 'Garamond', 'EB Garamond', 'Candara', serif;
          font-weight: bold;
        }
        
        .font-subheading {
          font-family: 'Garamond', 'EB Garamond', 'Candara', serif;
          font-style: italic;
        }
        
        .font-section {
          font-family: 'Proxima Nova Condensed', 'Oswald', sans-serif;
        }
        
        .font-quote {
          font-family: 'Cormorant Garamond', 'EB Garamond', serif;
        }
        
        .font-caption {
          font-family: 'Proxima Nova', 'Oswald', sans-serif;
          font-weight: 300;
        }
        
        .font-body {
          font-family: 'Garamond', 'EB Garamond', 'Candara', serif;
        }
        
        .text-primary {
          color: #004161;
        }
        
        .text-secondary {
          color: #cfb9a2;
        }
        
        .text-accent {
          color: #0f8f8c;
        }
        
        .bg-primary {
          background-color: #004161;
        }
        
        .bg-secondary {
          background-color: #cfb9a2;
        }
        
        .bg-accent {
          background-color: #0f8f8c;
        }
        
        .border-primary {
          border-color: #004161;
        }
        
        .border-secondary {
          border-color: #cfb9a2;
        }
        
        .border-accent {
          border-color: #0f8f8c;
        }
        
        .accent-subtle-bg {
          background-color: #f1eceb;
        }
        
        .accent-light-bg {
          background-color: #b1c3cf;
        }
        
        @page {
          size: 210mm 297mm;
          margin: 0;
        }
        
        @media print {
          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
            overflow: hidden;
          }
          
          #certificate-content {
            width: 210mm;
            height: 297mm;
            box-sizing: border-box;
            overflow: hidden;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          
          .content-wrapper {
            width: 210mm;
            height: 297mm;
            padding: 10mm;
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          
          .certificate-container {
            width: 100%;
            height: 100%;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 5mm;
          }
          
          .p-16 {
            padding: 1rem !important;
          }
          
          .p-12 {
            padding: 0.8rem !important;
          }
          
          .mb-12 {
            margin-bottom: 0.7rem !important;
          }
          
          .mb-8 {
            margin-bottom: 0.5rem !important;
          }
          
          .h-24 {
            height: 4.5rem !important;
          }
          
          .requirements-section {
            font-size: 0.7rem !important;
            padding: 0.5rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .requirements-section ul {
            margin: 0 !important;
            padding-left: 1rem !important;
          }
          
          .signature-section {
            margin-top: 0.5rem !important;
          }
          
          .seal {
            bottom: 0.5rem !important;
            right: 0.5rem !important;
          }
        }
      </style>
    </head>
    <body>
      <div id="certificate-content">
        <div class="content-wrapper">
          <div class="certificate-container border-8 border-[#004161]/5 p-8 relative bg-white">
            <!-- Background Pattern -->
            <div class="absolute inset-0 opacity-[0.02] pointer-events-none" style="background-image: url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');"></div>

            <!-- Border Design -->
            <div class="absolute inset-0 border-[16px] border-double border-[#004161]/10 pointer-events-none"></div>

            <!-- Logo -->
            <div class="text-center mb-4 relative">
              <img 
                src="/lovable-uploads/66c512e5-a31a-46e0-a418-2f8ccecc568d.png"
                alt="NWSISD Logo"
                class="h-20 mx-auto mb-4"
              />
            </div>
            
            <!-- Header -->
            <div class="text-center mb-4 relative">
              <h1 class="text-3xl font-bold text-[#004161] mb-2 font-heading">Certificate of Completion</h1>
              <div class="h-1 w-32 bg-gradient-to-r from-[#004161]/20 via-[#004161] to-[#004161]/20 mx-auto"></div>
            </div>
            
            <!-- Main Content -->
            <div class="text-center mb-4 relative">
              <p class="text-lg text-gray-600 mb-1 italic font-heading">This certifies that</p>
              <h2 class="text-2xl font-playfair mb-1 text-[#004161] px-4 py-1 inline-block relative font-title">
                ${certificateData.recipientName}
                <div class="absolute bottom-0 left-0 right-0 h-px bg-[#004161]/10"></div>
              </h2>
              <p class="text-lg text-gray-600 mb-1 font-heading">has successfully completed</p>
              <h3 class="text-xl font-bold text-[#004161] mb-1 font-heading">${certificateData.courseTitle}</h3>
              <p class="text-md text-gray-600 mb-1 font-heading">Presented by: ${certificateData.presenter}</p>
              <div class="flex items-center justify-center gap-2 text-lg text-gray-600 font-heading">
                <span>Total Number of Approved Clock Hours Earned: ${certificateData.hours}</span>
              </div>
            </div>

            <!-- Details -->
            <div class="grid grid-cols-3 gap-3 mb-4 text-center text-gray-600">
              <div class="p-2 bg-[#f1eceb] rounded-lg">
                <p class="font-semibold text-[#004161] font-section">Date</p>
                <p class="font-heading">${new Date(certificateData.completionDate).toLocaleDateString()}</p>
              </div>
              <div class="p-2 bg-[#f1eceb] rounded-lg">
                <p class="font-semibold text-[#004161] font-section">Location</p>
                <p class="font-heading">${certificateData.location}</p>
              </div>
              <div class="p-2 bg-[#f1eceb] rounded-lg">
                <p class="font-semibold text-[#004161] font-section">Time</p>
                <p class="font-heading">${certificateData.time}</p>
              </div>
            </div>

            <!-- State Requirements Section -->
            ${certificateData.stateRequirements ? `
              <div class="mb-3 text-sm text-gray-600 p-3 bg-[#f1eceb] rounded-lg requirements-section">
                <p class="font-semibold mb-1 text-[#004161] font-section">This training meets the State requirement for:</p>
                <ul class="list-none space-y-0 font-heading">
                  ${certificateData.stateRequirements.behavioralIntervention ? 
                    `<li>☒ Positive Behavioral Intervention Strategies${certificateData.stateRequirements.behavioralInterventionHours ? 
                      ` (${certificateData.stateRequirements.behavioralInterventionHours} hours)` : ''}</li>` : ''}
                  ${certificateData.stateRequirements.readingPreparation ? 
                    `<li>☒ Reading Preparation${certificateData.stateRequirements.readingPreparationHours ? 
                      ` (${certificateData.stateRequirements.readingPreparationHours} hours)` : ''}</li>` : ''}
                  ${certificateData.stateRequirements.keyWarningSigns ? 
                    `<li>☒ Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents${certificateData.stateRequirements.keyWarningSignsHours ? 
                      ` (${certificateData.stateRequirements.keyWarningSignsHours} hours)` : ''}</li>` : ''}
                  ${certificateData.stateRequirements.suicidePrevention ? 
                    `<li>☒ Suicide Prevention${certificateData.stateRequirements.suicidePreventionHours ? 
                      ` (${certificateData.stateRequirements.suicidePreventionHours} hours)` : ''}</li>` : ''}
                  ${certificateData.stateRequirements.englishLearners ? 
                    `<li>☒ English Learners${certificateData.stateRequirements.englishLearnersHours ? 
                      ` (${certificateData.stateRequirements.englishLearnersHours} hours)` : ''}</li>` : ''}
                  ${certificateData.stateRequirements.americanIndianHistory ? 
                    `<li>☒ American Indian History and Culture${certificateData.stateRequirements.americanIndianHistoryHours ? 
                      ` (${certificateData.stateRequirements.americanIndianHistoryHours} hours)` : ''}</li>` : ''}
                  ${certificateData.stateRequirements.culturalCompetency?.enabled ? `
                    <li>☒ Cultural Competency${certificateData.stateRequirements.culturalCompetencyHours ? 
                      ` (${certificateData.stateRequirements.culturalCompetencyHours} hours)` : ''}
                      <ul class="ml-4 mt-0">
                        ${certificateData.stateRequirements.culturalCompetency.racial ? 
                          `<li>☒ Racial, Cultural, and Socioeconomic Groups${certificateData.stateRequirements.culturalCompetency.racialHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.racialHours} hours)` : ''}</li>` : ''}
                        ${certificateData.stateRequirements.culturalCompetency.religion ? 
                          `<li>☒ Religion${certificateData.stateRequirements.culturalCompetency.religionHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.religionHours} hours)` : ''}</li>` : ''}
                        ${certificateData.stateRequirements.culturalCompetency.systemicRacism ? 
                          `<li>☒ Systemic Racism${certificateData.stateRequirements.culturalCompetency.systemicRacismHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.systemicRacismHours} hours)` : ''}</li>` : ''}
                        ${certificateData.stateRequirements.culturalCompetency.genderIdentity ? 
                          `<li>☒ Gender Identity, Including Transgender Students${certificateData.stateRequirements.culturalCompetency.genderIdentityHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.genderIdentityHours} hours)` : ''}</li>` : ''}
                        ${certificateData.stateRequirements.culturalCompetency.sexualOrientation ? 
                          `<li>☒ Sexual Orientation${certificateData.stateRequirements.culturalCompetency.sexualOrientationHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.sexualOrientationHours} hours)` : ''}</li>` : ''}
                        ${certificateData.stateRequirements.culturalCompetency.languageDiversity ? 
                          `<li>☒ Language Diversity${certificateData.stateRequirements.culturalCompetency.languageDiversityHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.languageDiversityHours} hours)` : ''}</li>` : ''}
                        ${certificateData.stateRequirements.culturalCompetency.disabilities ? 
                          `<li>☒ Individuals with Disabilities and Mental Health Concerns${certificateData.stateRequirements.culturalCompetency.disabilitiesHours ? 
                            ` (${certificateData.stateRequirements.culturalCompetency.disabilitiesHours} hours)` : ''}</li>` : ''}
                      </ul>
                    </li>` : ''}
                </ul>
              </div>` : ''}

            <!-- Signature -->
            <div class="flex items-center justify-between signature-section mt-auto">
              <div class="flex-1">
                <div class="w-48">
                  <img 
                    src="/lovable-uploads/27ee0fde-6ca5-4d1a-9f07-399dc352edc1.png"
                    alt="Heather Lyke Signature"
                    class="w-48 h-auto mb-1 signature-img"
                    style="margin-bottom: -5px;"
                  />
                </div>
                <div class="border-b-2 border-gray-400 w-48 mb-1"></div>
                <p class="font-semibold text-[#004161] font-heading">Heather Lyke</p>
                <p class="text-gray-600 font-heading">Executive Director</p>
                <p class="text-gray-600 font-heading">Northwest Suburban Integration School District</p>
              </div>
              <!-- Official Seal Watermark -->
              <div class="absolute bottom-8 right-8 opacity-[0.15] pointer-events-none seal">
                <div class="w-32 h-32 border-8 border-[#004161] rounded-full flex items-center justify-center p-4 relative seal-inner">
                  <img 
                    src="/lovable-uploads/be9c26c5-3086-4bfa-a7f9-294568213b2f.png"
                    alt="NWSISD Seal"
                    class="w-20 h-20 object-contain seal-img"
                  />
                  <div class="absolute inset-0 rounded-full border-4 border-[#004161] opacity-20"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <script>
        window.onload = () => {
          // Set page title to match filename
          document.title = 'NWSISD-Certificate-${new Date(certificateData.completionDate).toISOString().split('T')[0]}';
          
          // Force print after slight delay to ensure proper rendering
          setTimeout(() => {
            window.print();
          }, 1000);
        };
      </script>
    </body>
  </html>
`;

export const generateCertificate = async (
  supabase: SupabaseClient,
  certificateId: string
) => {
  const { data, error } = await supabase.functions.invoke('generate-certificate', {
    body: { certificateId }
  });

  if (error) throw error;

  return {
    recipientName: data.certificate.recipientName,
    courseTitle: data.certificate.courseTitle,
    completionDate: data.certificate.completionDate,
    hours: data.certificate.hours,
    presenter: data.certificate.presenter,
    location: data.certificate.location,
    time: data.certificate.startTime && data.certificate.endTime 
      ? `${data.certificate.startTime} - ${data.certificate.endTime}`
      : '12:30 - 3:30pm',
    stateRequirements: data.certificate.stateRequirements
  };
};
